import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Subject, Subscription, combineLatest, forkJoin } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';
import { AssetClassWarehouseDeleted, AssetClassWarehouseDeletedFromAdminTrash, AssetClassWarehouseDeletedFromTrash, AssetClassWarehouseDuplicate, AssetClassWarehouseOnServerAdminRestored, AssetClassWarehouseOnServerRestored, AssetClassWarehousePageRequested, AssetClassWarehouseTrashFlushed } from 'src/app/core/comparable/_actions/asset-class-warehouse.actions';
import { AssetClassWarehouseDataSource } from 'src/app/core/comparable/_data-sources/asset-class-warehouse.datasource';
import { AssetClassWarehouseModel } from 'src/app/core/comparable/_models/asset-class-warehouse.model';
import { selectAdminTrashedAssetClassWarehouseCount, selectAssetClassWarehouseLastQuery, selectTrashedAssetClassWarehouseCount } from 'src/app/core/comparable/_selectors/asset-class-warehouse.selectors';
import { AssetClassWarehouseService } from 'src/app/core/comparable/_services/asset-class-warehouse.service';
import { FileUploadService } from 'src/app/core/file-upload';
import { hasPermission } from 'src/app/core/mad-auth/mad-auth.store';
import { AppState } from 'src/app/core/reducers';
import { LayoutUtilsService, QueryParamsModel } from 'src/app/core/_base/crud';
import { fetchMapMarkers } from 'src/app/core/_base/crud/utils/map-marker.util';
import { ComparableMarkerLayer, MarkerLayerPin } from 'src/app/core/map/comparable-marker-layer';
import { ComparableMapPin, ComparableMapService } from 'src/app/core/comparable/_services/comparable-map.service';
import { emptyFilter, FilterChange, FilterModel } from '../../../shared_components/filter-section/filter-section.component';
import { MapMarkerInfoWindowComponent, MapMarkerInfoWindowDialogInput } from '../../../shared_components/map-marker-info-window/map-marker-info-window.component';
import { ToMarkerItemConverterService } from '../../../shared_components/map-marker-info-window/to-marker-item-converter-service.service';
import { MarkerInfoDialogComponent } from '../../_sub/map-info-dialog/marker-info-dialog.component';
import { WarehouseFilterDialogComponent } from '../_sub/warehouse-filter-dialog/warehouse-filter-dialog.component';
import { environment } from 'src/environments/environment';
import { MapsService } from 'src/app/core/map/maps.service';
import {AgmMap, MapsAPILoader} from '@agm/core';
import { FileDropService } from '../../../shared_components/upload-file/file-drop-dialog/file-drop.service';

@Component({
  selector: 'kt-warehouse-map',
  templateUrl: './warehouse-map.component.html',
  styleUrls: ['./warehouse-map.component.scss']
})
export class WarehouseMapComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('sort', {static: true}) sort: MatSort;
  @ViewChild('fileInput') fileInput;

  @ViewChild('search', {static: false})
  public searchElementRef: ElementRef;
  private geoCoder = new google.maps.Geocoder();

  dataSource: AssetClassWarehouseDataSource;
  displayedColumns = ['id', 'sub_type', 'size', 'consideration', 'source_date', 'actions'];

  filterCnt: number = 0;
  filterModel: FilterModel = emptyFilter();
  filter$: BehaviorSubject<FilterModel> = new BehaviorSubject(this.filterModel);
  filterObs$ = this.filter$.asObservable();

  lastQueryParams: QueryParamsModel;

  /**
   * One entry per distinct coordinate, kept here rather than rendered through the template:
   * `ComparableMarkerLayer` draws them straight onto the map. Held so a layer created after
   * the first response still gets what has already been fetched.
   */
  private _pins: MarkerLayerPin<ComparableMapPin>[] = []
  private _markerLayer: ComparableMarkerLayer<ComparableMapPin> = null
  /**
   * Drives the map's own request. The table's request is paginated; the map's is not, so
   * the two cannot share one -- with 25 rows a page the map used to draw 25 markers and
   * silently drop every other comparable in the viewport.
   */
  private _mapFilter$ = new Subject<FilterModel>();
  /**
   * Drives the table's page request. AGM emits boundsChange continuously while the map is
   * panned or zoomed, and each one used to dispatch a page request of its own -- a burst of
   * them per drag, against the list endpoint that reads every row's relations.
   */
  private _listRequest$ = new Subject<void>();
  private _centerLat$ = new BehaviorSubject<number>(null)
  centerLat$ = combineLatest([
    this._centerLat$,
    this.mapsService.latitude$
  ]).pipe(map(([initial, def]) => initial == null ? def : initial))
  private _centerLng$ = new BehaviorSubject<number>(null)
  centerLng$ = combineLatest([
    this._centerLng$,
    this.mapsService.longitude$
  ]).pipe(map(([initial, def]) => initial == null ? def : initial))
  currentCenter: { lat: 0, lng: 0 };
  map: any;
  zoom = 15;
  isFirst = true;
  latLngSW: google.maps.LatLng;
  latLngNE: google.maps.LatLng;
  _north = new BehaviorSubject<number>(47.92399006317647);
  _south = new BehaviorSubject<number>(47.9096089375195);
  _east = new BehaviorSubject<number>(106.9476215928344);
  _west = new BehaviorSubject<number>(106.8833344071655);
  private modal;
  previous;
  selectedId: string;
  icon = {
      url: './assets/media/icons/my_marker.svg'
  };
  trashCnt = 0;
  adminTrashCnt = 0;
  fileType = 'application/json';

  menuItems = [
    {
      title: 'Import',
      icon: 'flaticon2-add',
      refId: 0,
      disabled: false
    },
    {
      title: 'Download Template',
      icon: 'flaticon2-download',
      refId: 1,
      disabled: false
    },
    {
      title: 'Export',
      icon: 'flaticon2-box-1',
      refId: 2,
      disabled: true
    }
  ];
  menuSubject = new BehaviorSubject<number>(-1);

  private _subscriptions: Subscription[] = [];

  canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private toMarkerItemService: ToMarkerItemConverterService,
    private layoutService: LayoutUtilsService,
    private service: AssetClassWarehouseService,
    private comparableMapService: ComparableMapService,
    private dialog: MatDialog,
    private fileUploadService: FileUploadService,
    private mapsService: MapsService,
    private mapsAPILoader: MapsAPILoader,
    private fileDrop: FileDropService,
  ) { }

  ngOnInit(): void {

    this.dataSource = new AssetClassWarehouseDataSource(this.store);
    const listSubscription = this._listRequest$.pipe(
        debounceTime(300)
    ).subscribe(() => this._dispatchList());
    this._subscriptions.push(listSubscription);

    const markersSubscription = fetchMapMarkers<ComparableMapPin>(
      this._mapFilter$.asObservable(),
      filter => this.comparableMapService.load(7, filter)
    ).subscribe(res => this._setMarkers(res));
    this._subscriptions.push(markersSubscription);


    this.lastQueryParams = new QueryParamsModel({});
    const lastQuerySubscription = this.store.pipe(
        select(selectAssetClassWarehouseLastQuery),
        take(1)
    ).subscribe(res => {
        if (res.isEmpty()) {
            return;
        }
        this.lastQueryParams = res;
        this._updateFilter(res.filter)
    });
    this._subscriptions.push(lastQuerySubscription);

    const trashSubscription = this.store.pipe(
        select(selectTrashedAssetClassWarehouseCount)
    ).subscribe(res => {
        this.trashCnt = res;
    });
    this._subscriptions.push(trashSubscription);

    const adminTrashSubscription = this.store.pipe(
        select(selectAdminTrashedAssetClassWarehouseCount)
    ).subscribe(res => {
        this.adminTrashCnt = res;
    });
    this._subscriptions.push(adminTrashSubscription);

    // menu observer
    const menuSubjectSubscribe = this.menuSubject.asObservable().subscribe(refId => {
      switch (refId) {
        case 0:
          this.addFiles();
          break;
        case 1:
          this.downloadJsonTemplate();
          break;
        case 2:
          // TODO: export comparables
          break;
        default:
            break;
      }
    });
    this._subscriptions.push(menuSubjectSubscribe);
  }

  ngOnDestroy(): void {
    if (this._markerLayer) {
      this._markerLayer.destroy()
    }
    this._subscriptions.forEach(s => s.unsubscribe());
  }


  // Map Related
  mapReady(event: any) {

    // agmMap
    this.map = event;
    this.map.controls[google.maps.ControlPosition.RIGHT_CENTER].push(document.getElementById('Profile'));

    this._markerLayer = new ComparableMarkerLayer<ComparableMapPin>(
        this.map,
        this.ngZone,
        {
            iconUrl: this.icon.url,
            labelClassName: (_pin, isStack) => isStack ? 'bg-op-stack' : 'bg-op'
        },
        pins => this.showInfo(pins)
    )
    this._markerLayer.setPins(this._pins)

    this.map.addListener('dragend', () => {
      this._centerLat$.next(this.currentCenter.lat);
      this._centerLng$.next(this.currentCenter.lng);

      // this.mapAction.next(true);
      // this.paginator.pageIndex = 0;
      this._loadList();
    });

    this.mapsAPILoader.load().then(() => {
        this.geoCoder = new google.maps.Geocoder();
        if (this.searchElementRef) {
        const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
            types: ['geocode']
        });
        autocomplete.addListener('place_changed', () => {
            this.ngZone.run(() => {
            // get the place result
            const place: google.maps.places.PlaceResult = autocomplete.getPlace();
            // verify result
            if (place.geometry === undefined || place.geometry === null) {
                return;
            }

            // set latitude, longitude and zoom
            this._centerLat$.next(place.geometry.location.lat())
            this._centerLng$.next(place.geometry.location.lng())
            this.zoom = 15;
            });
        });
        }
    });
  }
  centerChange($event: any) {
    if ($event) {
      this.currentCenter = {lat: $event.lat, lng: $event.lng};
    }
  }

  mapClick(event: any) {
    if (this.modal) {
      this.modal.close();
    }

    this.modal = null;
  }
  zoomChanged($event) {
    this.isFirst = true;
  }

    onFilterChange(event: FilterChange) {
        if (event.page != null) {
            this.paginator.pageIndex = event.page
        }
        // The filter section keeps its own copy of the model and never learns about the
        // map's viewport, so its filter always comes back with bounds cleared. Keeping the
        // bounds the map last reported is what stops a filter change fetching the world.
        this.filterModel = Object.assign({}, event.filter, {
            bounds: event.filter.bounds ? event.filter.bounds : this.filterModel.bounds
        }) as FilterModel;
        this._centerLat$.next(event.filter.centerLat);
        this._centerLng$.next(event.filter.centerLng);
        this._loadList();
        this._loadMapMarkers();
    }
  boundsChanged(_latLngBound) {
    this.latLngNE = _latLngBound.getNorthEast();
    this.latLngSW = _latLngBound.getSouthWest();

    const xPadding = (this.latLngNE.lat() - this.latLngSW.lat()) * 0.08;
    this._north.next(this.latLngNE.lat() - xPadding);
    this._south.next(this.latLngSW.lat() + xPadding);

    this._east.next(this.latLngNE.lng() - xPadding);
    this._west.next(this.latLngSW.lng() + xPadding);
    const _new = Object.assign({}, this.filterModel) as FilterModel;
        if (_new.bounds == null) {
            _new.bounds = {ne_lat: 0, sw_lat: 0, ne_lng: 0, sw_lng: 0}
        } else {
            _new.bounds = Object.assign({}, _new.bounds);
        }
        _new.bounds.sw_lat = _latLngBound.getSouthWest().lat();
        _new.bounds.ne_lat = _latLngBound.getNorthEast().lat();
        _new.bounds.sw_lng = _latLngBound.getSouthWest().lng();
        _new.bounds.ne_lng = _latLngBound.getNorthEast().lng();

    this.filterModel = _new;



    if (this.isFirst) {
      if (this.modal) {
        this.modal.close();
      }
      this.previous = null;
      this.isFirst = !this.isFirst;
      // this.paginator.pageIndex = 0;
    }
    this._loadList();
    this._loadMapMarkers();
  }

    /**
     * The markers carry only what a pin needs, so the records behind the one clicked are
     * fetched here -- a handful of comparables at one coordinate, not a viewport of them.
     */
    showInfo(pins: ComparableMapPin[]) {
        forkJoin(pins.map(pin => this.service.getAssetClass(pin.id))).subscribe(responses => {
            const data = this.toMarkerItemService.convert('comparable', responses.map(res => ({...res.data, ac_type: 7})));
            this.dialog.open<MapMarkerInfoWindowComponent, MapMarkerInfoWindowDialogInput>(MapMarkerInfoWindowComponent, {
                data: {
                    modal_title: data.modal_title,
                    entities: data.entities
                }
            });
        });
    }

    setLocation() {
      this.setCurrentLocation();
    }

    // Get Current Location Coordinates
    private setCurrentLocation() {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                if (position.coords.latitude && position.coords.longitude) {
                    this._centerLat$.next(position.coords.latitude);
                    this._centerLng$.next(position.coords.longitude);
                    this.zoom = 15;
                }
            });
        } else {
            alert('Position not available');
        }
    }
    getLabel(title) {
        return {
            color: '#000000',
            fontFamily: '',
            fontSize: '14px',
            fontWeight: 'bold',
            className: 'bg-op',
            text: title,
        };
    }


  // Comparable related
  addComparable() {
    this.editComparable(null);
  }
  editComparable(ac: AssetClassWarehouseModel) {
    if (ac) {
      this.router.navigate(['../edit', ac.id], {relativeTo: this.activatedRoute});
    } else {
      this.router.navigate(['../add'], {relativeTo: this.activatedRoute})
    }
  }
  deleteComparable(ac: AssetClassWarehouseModel) {
    this.store.dispatch(new AssetClassWarehouseDeleted({id: ac.id}));
  }
  getItemCssClassByStatus(status: number): string {
    switch (status) {
      case 0:
        return 'warning';
      default:
        return 'success';
    }
  }
  getItemStatusString(status: number): string {
    switch (status) {
      case 0:
        return 'Draft';
      default:
        return 'Validated';
    }
  }

  goToList() {
    this.router.navigate(['../'], {relativeTo: this.activatedRoute});
  }

  /** Rebuilt from scratch on every response, so markers never outlive the filter that produced them. */
  private _setMarkers(pins: ComparableMapPin[]) {
    const grouped = new Map<string, MarkerLayerPin<ComparableMapPin>>()
    pins.forEach(pin => {
      // A comparable recorded without coordinates has nowhere to sit on the map;
      // it stays in the list view only.
      if (pin.latitude == null || pin.longitude == null) return
      const key = pin.latitude + '_' + pin.longitude
      const existing = grouped.get(key)
      if (existing) {
        existing.title = existing.title + '/' + pin.refNum
        existing.items.push(pin)
      } else {
        grouped.set(key, {
          key,
          title: pin.refNum,
          latitude: pin.latitude,
          longitude: pin.longitude,
          items: [pin]
        })
      }
    })

    this._pins = Array.from(grouped.values())
    if (this._markerLayer) {
      this._markerLayer.setPins(this._pins)
    }
  }

  private _loadMapMarkers() {
    this._mapFilter$.next(Object.assign({}, this.filterModel) as FilterModel);
  }

  private _loadList() {
      this._listRequest$.next();
  }

  private _dispatchList() {
    let queryParams = null;
    if (this.lastQueryParams.isEmpty()) {
        const _filter = Object.assign({}, this.filterModel) as FilterModel;

        queryParams = new QueryParamsModel(
            _filter,
            this.sort.direction,
            this.sort.active,
            this.paginator.pageIndex + 1,
            this.paginator.pageSize
        );
    } else {
        queryParams = this.lastQueryParams;
    }
    this.lastQueryParams = new QueryParamsModel({});
    this.store.dispatch(new AssetClassWarehousePageRequested({page: queryParams}));
  }
  private _updateFilter(filter: FilterModel) {
    this.filterModel = filter;
    this.filter$.next(filter);
  }
  trash() {
    this.service.getTrashed().subscribe(res => {
        const items = [];
        res.data.forEach(elem => {
            items.push({
                text: `#${elem.ref_num}`,
                id: elem.id.toString(),
                date: elem.user_deleted,
                hasPermanentlyDelete: true,
                deletedUser: elem.userDeletedBy
            });
        });
        this._showTrashed(items);
    })
  }
  adminTrash() {
    this.service.getAdminTrashed().subscribe(res => {
        const items = [];
        res.data.forEach(elem => {
            items.push({
                text: `#${elem.ref_num}`,
                id: elem.id.toString(),
                date: elem.deleted_at,
                hasPermanentlyDelete: true,
                deletedUser: elem.deletedBy
            });
        });
        this._showTrashed(items, true);
    })
  }
  delete(id: number) {
      this.store.dispatch(new AssetClassWarehouseDeletedFromTrash({id}))
  }
  restore(id: number, isAdmin = false) {
      if (isAdmin) {
        this.store.dispatch(new AssetClassWarehouseOnServerAdminRestored({id: id}));
      } else {
        this.store.dispatch(new AssetClassWarehouseOnServerRestored({id: id}));
      }
  }
  hardDelete(id: number) {
    this.store.dispatch(new AssetClassWarehouseDeletedFromAdminTrash({id: id}))
  }
  private _showTrashed(items, isAdmin = false) {
      let _title = 'Deleted Warehouse Comparables';
      if (isAdmin) {
          _title = 'Admin Deleted Warehouse Comparables';
      }
      this.layoutService.trashedEntities(_title, items, this, isAdmin);
  }

  addFiles() {
      this.fileDrop.pick({
          title: 'Import comparable',
          accept: this.fileType,
          multiple: false
      }).subscribe(files => this.handleImportFile(files[0]));
  }

  importComparable(event: any) {
    this.handleImportFile(event.target.files[0]);
  }

  private handleImportFile(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    this.fileUploadService.uploadCompJson(formData).subscribe(
      (res) => {
        if (!res) {
          return;
        }
        if (res.success) {
          this._loadList();
        }
      },
      (err) => console
    );
  }
  downloadJsonTemplate() {
    this.fileUploadService.compTemplateDownload(7).subscribe(res => {
        if (res.type == 4) {
          let blob = new Blob([res.body], { type: 'application/json'});
          let url = window.URL.createObjectURL(blob);
          var anchor = document.createElement("a");
          anchor.download = "interval-comp-warehouse-template.json";
          anchor.href = url;
          anchor.click();
        }
      },
      err => {
        alert("Problem while downloading the file.");
      }
    );
  }

  flush() {
    this.store.dispatch(new AssetClassWarehouseTrashFlushed());
  }

  duplicate(id: number) {
    this.store.dispatch(new AssetClassWarehouseDuplicate({id}))
  }
}
